import React from 'react';
import { Grid } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import get from 'lodash/get';
import { heightStyle, marginBottomSize } from 'utility/sizeStyle';
import dayjs from 'dayjs';
import ReactDatepicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './style.css';
import { CalendarIcon } from '@heroicons/react/outline';

interface DatePickerProps {
  disabled?: boolean;
  name: string;
  placeholder?: string;
  required?: boolean;
  label: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  alignItems?: 'center' | 'stretch' | 'start' | 'end' | 'baseline';
  spacing?: number;
  onChange?: (val: Date, event: React.SyntheticEvent) => void;
  isFuture?: boolean;
  visible?: boolean;
  showInlineError?: boolean;
  wrap?: boolean;
  readOnly?: boolean;
  minDate?: string | Date;
  value?: string | Date | null;
}

function DatePicker(props: DatePickerProps) {
  const {
    name, required, label, disabled, size, alignItems, spacing, onChange, isFuture, visible, showInlineError, wrap, readOnly, placeholder, minDate, value,
    ...rest
  } = props;

  const {
    control, formState: { errors }, setError, clearErrors,
  } = useFormContext();

  const disabledStyle = disabled ? 'bg-gray-50 border-dashed cursor-not-allowed text-inherit' : 'hover:border-gray-500';
  const readOnlyStyle = readOnly ? 'text-black bg-gray-50 active:ring-transparent focus:ring-transparent active:border-gray-200 focus:border-gray-200' : '';
  const errorStyle = get(errors, name) ? 'focus:border-red-600 hover:border-red-600 border-red-600' : '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDateChange = (date: Date | null, field: any, event: React.SyntheticEvent) => {
    if (!isFuture && date && dayjs(date).isAfter(dayjs())) {
      setError(name, { type: 'required', message: 'Date cannot be greater than today' });
    } else {
      clearErrors(name);
    }

    field.onChange(date);

    if (onChange) onChange(date, event);
  };

  const inputWidth = () => {
    if (!label) return 12;
    if (wrap) return 8;
    return 12;
  };

  if (!visible) return null;

  return (
    <Grid container spacing={spacing || wrap ? 2 : 1} alignItems={alignItems} mb={marginBottomSize[size]}>
      <Grid item xs={wrap ? 4 : 12}>
        <span className={`text-${size}`}>{label}</span>
        {required && !disabled && <span className="text-red-600">*</span>}
      </Grid>
      <Grid item xs={inputWidth()}>
        <Controller
          name={name}
          control={control}
          defaultValue={null}
          rules={{
            required: required ? `Field '${label}' is required` : false,
          }}
          render={({ field }) => {
            let selectedDate = null;
            if (value !== undefined) {
              selectedDate = value && dayjs(value).isValid() ? dayjs(value).toDate() : null;
            } else {
              selectedDate = field.value && dayjs(field.value).isValid() ? dayjs(field.value).toDate() : null;
            }

            return (
              <ReactDatepicker
                name={name}
                onBlur={field.onBlur}
                selected={selectedDate}
                onChange={(date: Date, event: React.SyntheticEvent) => handleDateChange(date, field, event)}
                dateFormat="dd/MM/yyyy"
                disabled={disabled}
                readOnly={readOnly}
                todayButton="Today"
                showIcon
                toggleCalendarOnIconClick
                calendarIconClassName="!p-0 !flex !items-center !h-full !pl-1"
                icon={<CalendarIcon fontSize={12} />}
                isClearable={!disabled && !readOnly}
                minDate={minDate ? dayjs(minDate).toDate() : undefined}
                wrapperClassName="w-full"
                className={`
                  p-1 py-0 border text-${size} border-gray-200 w-full focus:border-gray-200 focus:ring-0
                  placeholder:text-gray-300 ${heightStyle[size]} ${readOnly ? readOnlyStyle : disabledStyle} ${errorStyle}
                `}
                placeholderText={!disabled && !readOnly ? placeholder : ''}
                showWeekNumbers
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                portalId="root"
                {...rest}
              />
            );
          }}
        />
      </Grid>
      {showInlineError && get(errors, name) && (
        <div className={`flex w-full justify-end text-red-600 text-${size} mb-${marginBottomSize[size]} mt-0.5`}>{get(errors, name).message?.toString()}</div>
      )}
    </Grid>
  );
}

DatePicker.defaultProps = {
  disabled: false,
  placeholder: 'dd/MM/yyyy',
  required: false,
  size: 'xs',
  alignItems: 'center',
  spacing: null,
  onChange: () => null,
  isFuture: true,
  visible: true,
  wrap: true,
  showInlineError: false,
  readOnly: false,
  minDate: undefined,
  value: undefined,
};

export default DatePicker;
